import { Grid, Card, CardHeader, CardContent, Button, Typography } from '@mui/material';
import { FormAutoCompleteMultiSelectDynamic } from '../../../components/hook-form/form-autocomplete-multi-select.dynamic.component';
import { contactApiKeyStore } from '../../../api/contact-api-key-store';
import { useEffect, useState } from 'react';
import { ContactsDialog } from '../../contacts/contacts.dialog';
import { Contact } from '../../../models/contact';
import { useFormContext, useWatch } from 'react-hook-form';
import { SiTrackGrid } from '../../../components/si-track-grid/si-track-grid';
import { columns } from '../../contacts/grid-columns';
import { ContactsDialogWithGridSelect } from '../../contacts/contact-diallog-with-grid-selection';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { UserPermissionName } from '../../../models/user-permission-name';
import { GuardedComponent } from '../../../components/guarded-component';
import { gridApiKeyStore } from '../../../api/grid-api-key-store';
import { GridColumnConfig, GridPreference } from '../../../models/gridCoumnConfig';
import { useSaveData } from '../../../hooks/useSaveData';
import { State } from '@progress/kendo-data-query';
import useDebounce from '../../../hooks/use-debounce';

const styling = {
  paperStyle: { padding: '20px', borderRadius: '20px' },
  headingStyle: { fontWeight: 'bold' },
};

interface ContactsFormProps {
  name: string;
  label: string;
  page?: string;
}

export const ContactsForm = ({ name, label, page }: ContactsFormProps) => {
  const [openDialog, setOpenDialog] = useState(false);
  const { setValue, getValues } = useFormContext();
  const [valueForTab, setValueForTab] = useState('view');
  const [editItem, setEditItem] = useState<Contact>();

  const { data, isLoading, isFetching } = useLoadData<ApiResponse<Contact[]>>(
    contactApiKeyStore.getCustomerContacts(getValues('customer.customerID')),
    !!getValues('customer.customerID') && page === 'create'
  );

  useWatch({
    name: ['officeContacts', 'customer.customerID'] || [],
  });

  useEffect(() => {
    if (data && !isLoading) {
      const filteredData = data?.data?.filter((d) => d.isMasterContacts);
      setValue('officeContacts', filteredData);
    }
  }, [data, isLoading]);

  const handleOpenAddContactForm = () => {
    setOpenDialog(true);
  };

  const handleCloseAddContactForm = (contacts: Contact[]) => {
    setOpenDialog(false);
    setValue(name, contacts);
    setValueForTab('view');
  };

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout,
  } = useLoadData<ApiResponse<GridPreference>>(gridApiKeyStore.getGridLayout('Office', 'Contact'));

  // const { isLoading, data: Offices, refetch } = useLoadData<ApiResponse<Contact[]>>(getAPIKeyStore());

  const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredContactList = (getValues(name) || []).filter((contact: any) =>
      Object.values(contact).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredContacts(filteredContactList || []);
  }, [debouncedSearchTerm, getValues(name)]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'Contact',
      pageName: 'Office',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'Contact',
      pageName: 'Office',
    };

    mutateAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  // ----------------------------------------------------------------------------------------

  return (
    <Card elevation={0}>
      <CardHeader
        className="subHead"
        title={
          <Typography variant="h3" color="textPrimary" style={styling.headingStyle}>
            CONTACTS
          </Typography>
        }
        action={
          <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_CONTACT]}>
            <Button
              className="btnLink"
              sx={{ p: 0, m: 0, mr: 1 }}
              variant="text"
              onClick={() => handleOpenAddContactForm()}
            >
              Map Contact
            </Button>
          </GuardedComponent>
        }
      ></CardHeader>
      <CardContent>
        <Grid container>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            {/* <FormAutoCompleteMultiSelectDynamic
              name={name}
              label={label}
              dataItems={getValues(name)}
              watchKeys={['customer.customerID']}
              getQueryKeyConfig={() => contactApiKeyStore.getCustomerContacts(getValues('customer.customerID'))}
              optionsLabelKey="contactsFirstName"
              optionsValueKey="contactsID"
            /> */}
            <SiTrackGrid
              fileTitle='office_contact'
              // dataItems={getValues(name)}
              dataItemKey="contactsID"
              columns={columns}
              actionPermissions={{
                export: UserPermissionName.DOWNLOAD_GRID,
              }}
              // -----------------Grid Layout-------
              dataItems={filteredContacts || []}
              reorderable
              // handleSearch={handleInputChange}
              handleSaveLayout={handleSaveLayout}
              isSaveLayout={true}
              gridConfigFromApi={gridConfig}
              handleResetLayout={handleResetLayout}
            />
          </Grid>
        </Grid>
        {/* {openDialog && (
          <ContactsDialogW
            open={openDialog}
            onClose={handleCloseAddContactForm}
            contacts={getValues(name)}
            customerID={getValues('customer.customerID')}
          />
        )} */}
        {openDialog && (
          <ContactsDialogWithGridSelect
            open={openDialog}
            onClose={handleCloseAddContactForm}
            contacts={getValues(name)}
            customerID={getValues('customer.customerID')}
            setValue={setValueForTab}
            value={valueForTab}
            editItem={editItem}
            isSyncWithDux={getValues('syncWithDux')}
            setEditItem={setEditItem}
            watchKeys={['customer.customerID']}
            getQueryKeyConfig={() => contactApiKeyStore.getCustomerContacts(getValues('customer.customerID'))}
            page="office"
            pageType={page}
          />
        )}
      </CardContent>
    </Card>
  );
};
